'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@eliadmin/core/src/components/ui/select';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { changeLanguage } from '@eliadmin/core/src/server/api-client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getFlagEmoji } from '@eliadmin/core/src/utils/locale-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export function LanguageSelect() {
  const [isLoading, setIsLoading] = useState(false);
  const locale = useLocaleStore(s => s.currentLocale);
  const locales = useLocaleStore(s => s.supportedLocales);
  const [selectedLocale, setSelectedLocale] = useState(
    locales.find(localeIterator => localeIterator.locale === locale),
  );
  const defaultLocale = useLocaleStore(s => s.defaultLocale);
  const pathname = usePathname();

  const handleLocaleChange = async (newLocale: string) => {
    const localeCodes = locales.map(locale => locale.locale);
    const segments = pathname.split('/').filter(Boolean);
    const firstSegment = segments[0];
    const pathWithoutLocale =
      firstSegment && localeCodes.includes(firstSegment)
        ? `/${segments.slice(1).join('/')}`
        : pathname;
    const normalizedPath = pathWithoutLocale === '/' ? '' : pathWithoutLocale;
    const newRoute =
      newLocale === defaultLocale ? normalizedPath || '/' : `/${newLocale}${normalizedPath}`;

    setIsLoading(true);
    await changeLanguage({ locale: newLocale });
    window.location.replace(newRoute);
  };

  useEffect(() => {
    setSelectedLocale(locales.find(localeIterator => localeIterator.locale === locale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  return (
    <div className={cn('w-auto shrink-0', 'ea-language-select')}>
      <Select value={locale} onValueChange={handleLocaleChange}>
        <SelectTrigger
          aria-label={selectedLocale?.name || locale}
          className={cn(
            'my-1 h-8 w-12 rounded-none border-none bg-transparent p-0',
            'ea-language-select__trigger',
          )}
          disabled={isLoading}
          innerTriggerClassName="h-8 gap-1 px-0 py-0"
        >
          {isLoading ? (
            <Spinner className={cn('h-4 w-4', 'ea-language-select__spinner')} />
          ) : selectedLocale?.image?.url ? (
            <Image
              src={selectedLocale.image.url}
              alt={resolveImageAlt(selectedLocale?.image, selectedLocale?.locale || '')}
              className={cn('h-4 w-6 object-contain', 'ea-language-select__flag')}
              width={24}
              height={16}
            />
          ) : (
            <span className={cn('text-base leading-none', 'ea-language-select__flag')}>
              {getFlagEmoji(selectedLocale?.locale || locale) ?? selectedLocale?.locale ?? locale}
            </span>
          )}
        </SelectTrigger>
        <SelectContent
          align="end"
          sideOffset={8}
          className={cn('rounded-none shadow-none', 'ea-language-select__content')}
        >
          {locales.map(locale => (
            <SelectItem
              key={`${locale.name}-${locale.locale}`}
              value={locale.locale}
              className={cn(
                'flex items-center whitespace-nowrap rounded-none',
                'ea-language-select__item',
              )}
            >
              {locale.image ? (
                <span className="flex items-center whitespace-nowrap">
                  <Image
                    src={locale.image.url}
                    alt={resolveImageAlt(locale?.image, locale?.locale)}
                    className={cn('mr-2 h-3.5 w-5 object-contain', 'ea-language-select__flag')}
                    width={20}
                    height={14}
                  />
                  <span>{locale.name}</span>
                </span>
              ) : (
                <span className="flex items-center whitespace-nowrap">
                  <span className={cn('mr-2', 'ea-language-select__flag')}>
                    {getFlagEmoji(locale.locale) ?? locale.locale}
                  </span>
                  <span>{locale.name}</span>
                </span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
